import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import NavbarWithPushContent from './NavbarWithPushContent';
import useContactInfo from '../Hooks/useContactInfo';
import LoadingSpinner from '../Assets/LoadingSpinner';
import ErrorComponent from '../Assets/ErrorComponent';
import { useTranslation } from 'react-i18next';
import Reveal from '../Assets/Reveal';

type ProductCategory = {
    _id: string;
    name: string;
    subcategories: { name: string; products: any[] }[];
    products: any[];
};


interface ContactUsProps {
    products: ProductCategory[];
    lang: string
}

const MailIcon = () => (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
);

const ContactUs: React.FC<ContactUsProps> = ({ products, lang }) => {

    const { t } = useTranslation();
    const { contactInfos, loading, error, } = useContactInfo();

    if (loading) return <LoadingSpinner />
    if (error) return <ErrorComponent message={error} />

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <div className="hidden md:block">
                <NavbarWithPushContent products={products} lang={lang} />
            </div>

            <div className="block md:hidden">
                <Navbar products={products} lang={lang} />
            </div>

            {/* Page header */}
            <header className="bg-gray-100 px-4 pt-28 md:pt-32 pb-12">
                <div className="max-w-6xl mx-auto text-center">
                    <h1 className="text-4xl sm:text-5xl font-bold text-brand-gray">
                        {t('navbar.contacts')}
                    </h1>
                    <span className="mx-auto mt-5 block h-1 w-20 rounded-full bg-brand-400" />
                </div>
            </header>

            {/* CMS contact info blocks */}
            {contactInfos && contactInfos.length > 0 && (
                <section className="bg-gray-50 px-4 py-16">
                    <div className="max-w-6xl mx-auto space-y-12">
                        {contactInfos.map((info, index) => (
                            <Reveal key={index}>
                                <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-md">
                                    <div className={`grid items-center gap-8 p-6 sm:p-10 ${info.image ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
                                        <div>
                                            {info.name?.[lang] && (
                                                <>
                                                    <h2 className="text-2xl sm:text-3xl font-bold text-brand-gray">
                                                        {info.name[lang]}
                                                    </h2>
                                                    <span className="mt-4 mb-6 block h-1 w-12 rounded-full bg-brand-400" />
                                                </>
                                            )}
                                            <div
                                                className="prose max-w-none text-gray-600 [&_ul]:list-none [&_ul]:pl-0"
                                                dangerouslySetInnerHTML={{
                                                    __html: (info.description?.[lang] || '').replace(
                                                        /®/g,
                                                        '<sup style="font-size:20px;">®</sup>'
                                                    ),
                                                }}
                                            />
                                        </div>

                                        {info.image && (
                                            <div className="flex items-center justify-center">
                                                <img
                                                    src={info.image}
                                                    alt="Galafarm Facility"
                                                    className="w-full rounded-2xl shadow-md object-cover"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            {/* Contact methods */}
            <section className="bg-white px-4 py-16">
                <div className="max-w-6xl mx-auto">
                    <div className="mb-12 text-center">
                        <h2 className="text-3xl sm:text-4xl font-bold text-brand-gray">
                            {t('contactPage.title')}
                        </h2>
                        <span className="mx-auto mt-4 block h-1 w-20 rounded-full bg-brand-400" />
                    </div>

                    <div className="mx-auto grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
                        <Reveal delay={0} className="h-full">
                            <div className="group flex h-full flex-col items-start gap-3 rounded-2xl border border-gray-100 bg-white p-8 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-500 transition-colors duration-300 group-hover:bg-brand-400 group-hover:text-white">
                                    <MailIcon />
                                </div>
                                <h3 className="text-xl font-bold text-brand-gray">
                                    {t('contactPage.generalInfo')}
                                </h3>
                                <a
                                    href="mailto:info@galafarm.com.mk"
                                    className="font-semibold text-brand-600 transition-colors duration-200 hover:text-brand-700 break-all"
                                >
                                    info@galafarm.com.mk
                                </a>
                                <p className="text-sm text-gray-500">
                                    {t('contactPage.generalInfoDesc')}
                                </p>
                            </div>
                        </Reveal>

                        <Reveal delay={100} className="h-full">
                            <div className="group flex h-full flex-col items-start gap-3 rounded-2xl border border-gray-100 bg-white p-8 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-500 transition-colors duration-300 group-hover:bg-brand-400 group-hover:text-white">
                                    <MailIcon />
                                </div>
                                <h3 className="text-xl font-bold text-brand-gray">
                                    {t('contactPage.businessDev')}
                                </h3>
                                <a
                                    href="mailto:bd@galafarm.com.mk"
                                    className="font-semibold text-brand-600 transition-colors duration-200 hover:text-brand-700 break-all"
                                >
                                    bd@galafarm.com.mk
                                </a>
                                <p className="text-sm text-gray-500">
                                    {t('contactPage.businessDevDesc')}
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ContactUs;
